import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

type ConsentPreferences = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
};

const defaultPrefs: ConsentPreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false,
};

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState<ConsentPreferences>(defaultPrefs);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("ew_cookie_consent");
    if (!consent) {
      setTimeout(() => setVisible(true), 1200);
    }
  }, []);

  const saveConsent = async (allAccepted: boolean, customPrefs?: ConsentPreferences) => {
    const finalPrefs = allAccepted
      ? { necessary: true, analytics: true, marketing: true, preferences: true }
      : (customPrefs ?? prefs);

    localStorage.setItem("ew_cookie_consent", JSON.stringify(finalPrefs));
    localStorage.setItem("ew_cookie_consent_date", new Date().toISOString());

    try {
      const body = new URLSearchParams({
        necessary: String(finalPrefs.necessary),
        analytics: String(finalPrefs.analytics),
        marketing: String(finalPrefs.marketing),
        preferences: String(finalPrefs.preferences),
        consent_date: new Date().toISOString(),
        page_url: window.location.href,
      });
      await fetch("https://readdy.ai/api/form/d7fu82aa509fjl5l0lhg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
    } catch {
      // Silent fail — consent already saved locally
    }

    setSaved(true);
    setTimeout(() => setVisible(false), 600);
  };

  const handleRejectAll = () => {
    saveConsent(false, { necessary: true, analytics: false, marketing: false, preferences: false });
  };

  const handleAcceptAll = () => saveConsent(true);

  const handleSavePrefs = () => saveConsent(false, prefs);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6">
      <div
        className={`max-w-5xl mx-auto bg-white border border-stone-200 rounded-xl overflow-hidden transition-all duration-500 ${
          saved ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"
        }`}
        style={{ boxShadow: "0 -4px 30px rgba(0,0,0,0.1)" }}
      >
        {/* Main bar */}
        <div className="p-5 sm:p-6">
          <div className="flex flex-col lg:flex-row lg:items-start gap-5">
            <div className="flex items-start gap-3 flex-1">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                <i className="ri-shield-check-line text-amber-600 text-lg"></i>
              </div>
              <div>
                <h2 className="font-bold text-zinc-900 text-base mb-1" style={{ fontFamily: "'Raleway', sans-serif" }}>
                  We use cookies
                </h2>
                <p className="text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>
                  We use cookies to improve your experience, analyse site traffic, and personalise content. You can choose which cookies to accept. Necessary cookies are always enabled as they are essential for the website to function.{" "}
                  <Link to="/cookies" className="text-amber-600 hover:underline font-medium">Learn more</Link>
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row lg:flex-col gap-2 lg:min-w-[180px]">
              <button
                onClick={handleAcceptAll}
                className="bg-[#1C2B1F] hover:bg-[#253828] text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                style={{ fontFamily: "'Raleway', sans-serif" }}
              >
                Accept All
              </button>
              <button
                onClick={handleRejectAll}
                className="bg-stone-100 hover:bg-stone-200 text-zinc-700 text-sm font-semibold px-5 py-2.5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                style={{ fontFamily: "'Raleway', sans-serif" }}
              >
                Reject All
              </button>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-amber-600 hover:text-amber-700 text-sm font-medium text-left whitespace-nowrap cursor-pointer flex items-center gap-1"
                style={{ fontFamily: "'Raleway', sans-serif" }}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${showDetails ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-sm`}></i>
                </div>
                Manage Preferences
              </button>
            </div>
          </div>
        </div>

        {/* Detailed preferences */}
        {showDetails && (
          <div className="border-t border-stone-100 bg-stone-50 p-5 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              {[
                {
                  key: "necessary" as keyof ConsentPreferences,
                  label: "Strictly Necessary",
                  desc: "Required for the website to function. Cannot be disabled.",
                  forced: true,
                },
                {
                  key: "analytics" as keyof ConsentPreferences,
                  label: "Analytics",
                  desc: "Help us understand how visitors interact with our website.",
                  forced: false,
                },
                {
                  key: "marketing" as keyof ConsentPreferences,
                  label: "Marketing",
                  desc: "Used to personalise advertising and track campaign effectiveness.",
                  forced: false,
                },
                {
                  key: "preferences" as keyof ConsentPreferences,
                  label: "Preferences",
                  desc: "Remember your settings and customise your experience.",
                  forced: false,
                },
              ].map((item) => (
                <div key={item.key} className="flex items-start gap-3 bg-white rounded-lg p-4 border border-stone-200">
                  <div className="flex-1">
                    <p className="font-semibold text-zinc-900 text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>{item.label}</p>
                    <p className="text-zinc-500 text-xs mt-0.5" style={{ fontFamily: "'Raleway', sans-serif" }}>{item.desc}</p>
                  </div>
                  <button
                    onClick={() => {
                      if (!item.forced) {
                        setPrefs((p) => ({ ...p, [item.key]: !p[item.key] }));
                      }
                    }}
                    className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 mt-0.5 ${
                      item.forced || prefs[item.key] ? "bg-amber-500" : "bg-stone-300"
                    } ${item.forced ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                    aria-label={`Toggle ${item.label}`}
                  >
                    <span
                      className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                        item.forced || prefs[item.key] ? "translate-x-5" : "translate-x-0.5"
                      }`}
                    ></span>
                  </button>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button
                onClick={handleSavePrefs}
                className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-6 py-2.5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                style={{ fontFamily: "'Raleway', sans-serif" }}
              >
                Save My Preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
